import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button, TextField, Select, MenuItem, Stack } from '@mui/material';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { loadNotifications, sendNewNotification, markNotificationAsRead } from './notificationsSlice';
import { formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';

const STATUS_COLOR = { sent: tokens.severity.low, pending: tokens.text.muted, failed: tokens.severity.critical };

export default function NotificationsPage() {
  const dispatch = useDispatch();
  const { items } = useSelector((s) => s.notifications);
  const [channel, setChannel] = useState('email');
  const [recipient, setRecipient] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => { dispatch(loadNotifications()); }, [dispatch]);

  const sent = items.filter((n) => n.status === 'sent').length;
  const failed = items.filter((n) => n.status === 'failed').length;

  const handleSend = () => {
    if (!recipient.trim() || !message.trim()) return;
    dispatch(sendNewNotification({ channel, recipient: recipient.trim(), message: message.trim() }));
    setRecipient(''); setMessage('');
  };

  return (
    <Box>
      <PageHeader title="Notification Service" subtitle="Email, SMS, and WhatsApp — including automated alerts fired by the Automation & Response engine" />
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}><StatCard label="Total" value={items.length} icon={NotificationsActiveRoundedIcon} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Sent" value={sent} accent={tokens.severity.low} /></Grid>
        <Grid item xs={12} sm={4}><StatCard label="Failed" value={failed} accent={tokens.severity.critical} /></Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2.5 }}>
        <Stack direction="row" spacing={1.5}>
          <Select size="small" value={channel} onChange={(e) => setChannel(e.target.value)} sx={{ minWidth: 120 }}>
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="sms">SMS</MenuItem>
            <MenuItem value="whatsapp">WhatsApp</MenuItem>
          </Select>
          <TextField size="small" placeholder="Recipient" value={recipient} onChange={(e) => setRecipient(e.target.value)} />
          <TextField size="small" fullWidth placeholder="Message" value={message} onChange={(e) => setMessage(e.target.value)} />
          <Button variant="contained" onClick={handleSend}>Send</Button>
        </Stack>
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Channel', 'Recipient', 'Message', 'Status', 'When', 'Read'].map((h) => (
                <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.68rem', textTransform: 'uppercase' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((n) => (
              <TableRow key={n.id} hover>
                <TableCell sx={{ fontSize: '0.76rem', textTransform: 'capitalize' }}>{n.channel}</TableCell>
                <TableCell sx={{ fontSize: '0.76rem', color: tokens.text.secondary }}>{n.recipient}</TableCell>
                <TableCell sx={{ fontSize: '0.78rem', maxWidth: 320 }}>{n.message}</TableCell>
                <TableCell><Chip label={n.status} size="small" sx={{ color: STATUS_COLOR[n.status], border: `1px solid ${STATUS_COLOR[n.status]}`, backgroundColor: 'transparent' }} /></TableCell>
                <TableCell sx={{ fontSize: '0.7rem', color: tokens.text.muted, whiteSpace: 'nowrap' }}>{formatRelativeTime(n.createdAt)}</TableCell>
                <TableCell>{n.readByRecipient ? <Chip label="Read" size="small" /> : <Button size="small" onClick={() => dispatch(markNotificationAsRead(n.id))}>Mark read</Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
